// @erp/module-fin/fe · queryOptions accessor-factories (DEV-GUIDE-002 §3.2).
// Mirrors modules/cms/src/fe/queries.ts.
//
// THE PATTERN these prove: each factory takes PLAIN values (never an accessor)
// and is CALLED inside the options function at the screen —
//   const search = Route.useSearch();                          // accessor
//   const q = useQuery(() => finQueries.invoicesList(search()));   // called here
// Because the whole options function re-runs when search() changes, reactivity
// lives at the call site and the factory stays a pure value→queryOptions map.
//
// Every queryFn is `() => unwrap<DTO>(finApi.<path>.$get(...))`: the typed hc
// client builds the request, unwrap resolves ok→JSON or throws the wire's
// ApiError (@erp/api-client). DTOs are the schema-inferred wire types.
//
// staleTime tiers (ADR-001 §10.2 — factories override the portal's 30s default):
//   list      30s  — listings + the verification queue/stats (checker churn)
//   detail    60s  — a single invoice/payment churns less than a filtered list
//   reference  5m  — the receiving-accounts registry (config, rarely edited)
// proofServe stays on the detail tier — well inside its 300s presigned-GET TTL.
// keepPreviousData on every keyset list holds the current page visible while
// the next cursor loads (no flash to empty on paginate/filter).
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/solid-query";
import { unwrap } from "@erp/api-client";
import type { CursorQuery } from "@erp/core-schemas";
import type {
  FinAccountDto,
  FinInvoiceDto,
  FinInvoiceStats,
  FinLedgerDto,
  FinLedgerStats,
  FinPaymentDto,
  InvoiceListQuery,
  LedgerListQuery,
  ProofServeResult,
  VerificationListQuery,
  VerificationStats,
} from "../schemas/index.js";
// Type-only — erased at build; the api lane's composite response shapes stay
// the single source of truth (the fe/client.ts FinRoutes precedent).
import type { InvoiceDetail, QueuePaymentDto } from "../schemas/index.js";
import { finApi } from "./client.js";
import { finKeys } from "./keys.js";

/** Keyset page envelope (@erp/platform/db keysetPage) — every cursor-paged list returns this. */
export type KeysetPage<T> = { data: T[]; nextCursor: string | null };

/** GET /my/invoices/:id — the payer detail plus the ACTIVE receiving instruments ("how to pay"). */
export type MyInvoiceDetail = InvoiceDetail & { howToPay: FinAccountDto[] };

export type { InvoiceDetail, QueuePaymentDto };

const STALE = {
  list: 30_000,
  detail: 60_000,
  reference: 5 * 60_000,
} as const;

export const finQueries = {
  // ── Receiving accounts (console config — "how payers pay us") ───────────────
  /** The FULL instrument registry, active AND disabled (fin:read). Reference tier — config, rarely edited. */
  accountsList: () =>
    queryOptions({
      queryKey: finKeys.accounts.list(),
      queryFn: () => unwrap<FinAccountDto[]>(finApi.accounts.$get()),
      staleTime: STALE.reference,
    }),

  // ── Invoices (console, fin:read) ────────────────────────────────────────────
  /** Keyset list, filters (?status/?module/?search/?payer + cursor) ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. */
  invoicesList: (filters: InvoiceListQuery) =>
    queryOptions({
      queryKey: finKeys.invoices.list(filters),
      queryFn: () => unwrap<KeysetPage<FinInvoiceDto>>(finApi.invoices.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The SAME keyset register as ONE ACCUMULATING STREAM (2026-09-22, the owner:
   * "now let's do it with every table and card listing of every portal").
   *
   * NOTHING ABOUT THE SERVER CHANGES — same route, same `{data, nextCursor}`,
   * same filter projection as `invoicesList` above; the only difference is that
   * pages ACCUMULATE in one cache entry instead of replacing each other.
   *
   * · `initialPageParam: undefined` — the first page carries no cursor, exactly
   *   as the first `?cursor=`-less request always did.
   * · `getNextPageParam` reads the wire's own `nextCursor`; `null` (the last
   *   page) becomes `undefined`, which is how TanStack says "no more".
   * · The filters are the key MINUS the cursor — a cursor is a position inside
   *   the stream now, not part of its identity, so it has left the URL and the
   *   key alike. `"infinite"` on the end keeps this entry distinct from the
   *   paged `invoicesList(filters)` one, which the Overview's two capped scans
   *   still use — a bounded read of the newest due/partial charges for a KPI
   *   tile, not a list anybody pages. (`ledgerList`, `verificationList`,
   *   `myInvoices` and `myPayments` have no fe consumer at all since their
   *   screens converted; they stay published affordances, and retiring them is
   *   a deliberate decision rather than a side effect of this change.)
   * · NO `keepPreviousData`: a facet change starts a NEW stream, and holding the
   *   old charges under the new controls would show a list that does not match
   *   them. The table shows its skeleton rows instead.
   */
  invoicesListInfinite: (filters: Omit<InvoiceListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...finKeys.invoices.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<FinInvoiceDto>>(finApi.invoices.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<FinInvoiceDto>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /**
   * The register's figures — GET /invoices/stats: every charge counted by where it
   * stands, plus what is still owed, INSTITUTION-WIDE (never the rows on a page; a
   * keyset page is not a total, ADR-003 §4). Under invoices.all(), so create/void and
   * every verify that recomputes a status refresh them with the list.
   */
  invoicesStats: () =>
    queryOptions({
      queryKey: finKeys.invoices.stats(),
      queryFn: () => unwrap<FinInvoiceStats>(finApi.invoices.stats.$get()),
      staleTime: STALE.list,
    }),

  /** One invoice + its payments + its append-only ledger slice — the console detail panel. */
  invoice: (id: string) =>
    queryOptions({
      queryKey: finKeys.invoices.detail(id),
      queryFn: () => unwrap<InvoiceDetail>(finApi.invoices[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  // ── Ledger (console journal view, fin:read) ─────────────────────────────────
  /** The append-only journal — keyset on (at, id) DESC; ?invoiceId narrows to one charge's history. */
  ledgerList: (filters: LedgerListQuery) =>
    queryOptions({
      queryKey: finKeys.ledger.list(filters),
      queryFn: () => unwrap<KeysetPage<FinLedgerDto>>(finApi.ledger.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The journal as ONE ACCUMULATING STREAM (2026-09-22) — same route, same keyset on (at, id) DESC; `?invoiceId` is the key, the cursor is not. See `invoicesListInfinite`. */
  ledgerListInfinite: (filters: Omit<LedgerListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...finKeys.ledger.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<FinLedgerDto>>(finApi.ledger.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<FinLedgerDto>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The journal's figures — GET /ledger/stats: every line ever, and the money charged / received / reversed since the financial year opened (the invoicesStats precedent). */
  ledgerStats: () =>
    queryOptions({
      queryKey: finKeys.ledger.stats(),
      queryFn: () => unwrap<FinLedgerStats>(finApi.ledger.stats.$get()),
      staleTime: STALE.list,
    }),

  // ── Verification queue (fin:verify OR module :desk/:admin — server auto-scoped) ──
  /** Tabbed checker queue (pending/verified/declined + ?module/?search). Rows carry proof METADATA; bytes via proofServe. */
  verificationList: (filters: VerificationListQuery) =>
    queryOptions({
      queryKey: finKeys.verification.list(filters),
      queryFn: () => unwrap<KeysetPage<QueuePaymentDto>>(finApi.verification.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The checker queue as ONE ACCUMULATING STREAM (2026-09-22) — same route, same tab/module/search projection; the tab is part of the key, the cursor is not. See `invoicesListInfinite`. */
  verificationListInfinite: (filters: Omit<VerificationListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...finKeys.verification.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<QueuePaymentDto>>(finApi.verification.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<QueuePaymentDto>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The KPI stat row (counts + awaiting/net-collected paise, scoped like the queue). List tier — checker-desk churn. */
  verificationStats: () =>
    queryOptions({
      queryKey: finKeys.verification.stats(),
      queryFn: () => unwrap<VerificationStats>(finApi.verification.stats.$get()),
      staleTime: STALE.list,
    }),

  // ── The payer surface (identity-scoped server-side — any signed-in session) ──
  /** My invoices — keyset page scoped to invoice.payer_user_id = session (the server enforces; no filter can widen it). */
  myInvoices: (filters: CursorQuery) =>
    queryOptions({
      queryKey: finKeys.my.invoices(filters),
      queryFn: () => unwrap<KeysetPage<FinInvoiceDto>>(finApi.my.invoices.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** My invoices as ONE ACCUMULATING STREAM (2026-09-22) — the same identity-scoped route, read through `useInfiniteQuery`. See `invoicesListInfinite`. */
  myInvoicesInfinite: (filters: Omit<CursorQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...finKeys.my.invoices(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<FinInvoiceDto>>(finApi.my.invoices.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<FinInvoiceDto>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** My invoice detail + payments + ledger + howToPay (the ACTIVE instruments). 403 FIN_NOT_PAYER off-identity. */
  myInvoice: (id: string) =>
    queryOptions({
      queryKey: finKeys.my.invoiceDetail(id),
      queryFn: () => unwrap<MyInvoiceDetail>(finApi.my.invoices[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** My payment history — statuses, rejection reasons, receipt numbers when verified. */
  myPayments: (filters: CursorQuery) =>
    queryOptions({
      queryKey: finKeys.my.payments(filters),
      queryFn: () => unwrap<KeysetPage<FinPaymentDto>>(finApi.my.payments.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** My payment claims as ONE ACCUMULATING STREAM (2026-09-22) — the same identity-scoped route. See `invoicesListInfinite`. */
  myPaymentsInfinite: (filters: Omit<CursorQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...finKeys.my.payments(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<FinPaymentDto>>(finApi.my.payments.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<FinPaymentDto>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ── Proof serving (by PROOF ID, never raw key) ──────────────────────────────
  /**
   * A short-TTL presigned GET for one proof (inline images, attachment pdf) —
   * viewer = submitter | fin:read | the module verifier tier, enforced
   * server-side. Detail tier (60s) keeps refetches comfortably inside the
   * 300s URL lease; a screen renders `.url` directly in an <img>/<a>.
   */
  proofServe: (id: string) =>
    queryOptions({
      queryKey: finKeys.proofs.serve(id),
      queryFn: () => unwrap<ProofServeResult>(finApi.proofs[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),
} as const;

export type FinQueries = typeof finQueries;
